import React, { useState } from 'react';
import { Accessibility, Eye, Download, Copy, CheckCircle, XCircle, Star, Users, Target, AlertTriangle } from 'lucide-react';

export const AccessibilityRules: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'contrast' | 'typography' | 'images' | 'interaction' | 'guidelines'>('contrast');

  const AccessibilityExample: React.FC<{
    title: string;
    description: string;
    example: React.ReactNode;
    compliance: string;
    rationale: string;
  }> = ({ title, description, example, compliance, rationale }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Compliance:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{compliance}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Example:</p>
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
      
      <div>
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Rationale:</p>
        <p className="text-sm text-charcoal-700">{rationale}</p>
      </div>
    </div>
  );

  const ContrastExample: React.FC<{
    title: string;
    foreground: string;
    background: string;
    ratio: string;
    level: string;
    example: React.ReactNode;
  }> = ({ title, foreground, background, ratio, level, example }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">Foreground: {foreground} | Background: {background}</p>
        <p className="text-xs text-charcoal-500 mt-1">Ratio: {ratio} | Level: {level}</p>
      </div>
      
      <div className="mb-4">
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
      
      <div className="flex gap-2">
        <button className="flex items-center gap-1 px-4 py-2 bg-consulting-slate-blue-600 text-white text-sm font-semibold hover:bg-consulting-slate-blue-700 transition-colors">
          <Eye className="h-4 w-4" />
          Test Contrast
        </button>
        <button className="flex items-center gap-1 px-4 py-2 bg-[var(--hover-tint)] text-charcoal-700 text-sm font-semibold hover:bg-[var(--hover-tint)] transition-colors">
          <Copy className="h-4 w-4" />
          Copy Colors
        </button>
      </div>
    </div>
  );

  const TypographyExample: React.FC<{
    title: string;
    description: string;
    size: string;
    weight: string;
    lineHeight: string;
    example: React.ReactNode;
    compliance: string;
  }> = ({ title, description, size, weight, lineHeight, example, compliance }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
        <p className="text-xs text-charcoal-500 mt-1">Size: {size} | Weight: {weight} | Line Height: {lineHeight}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Compliance:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{compliance}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Example:</p>
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
    </div>
  );

  const ImageExample: React.FC<{
    title: string;
    description: string;
    altText: string;
    example: React.ReactNode;
    compliance: string;
  }> = ({ title, description, altText, example, compliance }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Alt Text:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{altText}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Compliance:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{compliance}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Example:</p>
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
    </div>
  );

  const InteractionExample: React.FC<{
    title: string;
    description: string;
    example: React.ReactNode;
    compliance: string;
    rationale: string;
  }> = ({ title, description, example, compliance, rationale }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <div className="mb-4">
        <h3 className="text-lg font-bold text-charcoal-900">{title}</h3>
        <p className="text-sm text-charcoal-600">{description}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Compliance:</p>
        <p className="text-sm text-charcoal-700 bg-[var(--surface)] p-3">{compliance}</p>
      </div>
      
      <div className="mb-4">
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Example:</p>
        <div className="bg-[var(--surface)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
          {example}
        </div>
      </div>
      
      <div>
        <p className="text-sm font-semibold text-charcoal-600 mb-2">Rationale:</p>
        <p className="text-sm text-charcoal-700">{rationale}</p>
      </div>
    </div>
  );

  const DoDontExample: React.FC<{
    title: string;
    doExample: React.ReactNode;
    dontExample: React.ReactNode;
    explanation: string;
  }> = ({ title, doExample, dontExample, explanation }) => (
    <div className="bg-[var(--bg-raised)] border-2 border-[var(--rule)] p-6">
      <h3 className="text-lg font-bold text-charcoal-900 mb-4">{title}</h3>
      
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="h-4 w-4 text-sage-600" />
            <span className="text-sm font-semibold text-sage-600">DO</span>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4 h-24 flex items-center justify-center relative overflow-hidden">
            {doExample}
          </div>
        </div>
        
        <div>
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-4 w-4 text-[var(--mustard)]" />
            <span className="text-sm font-semibold text-[var(--mustard)]">DON'T</span>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4 h-24 flex items-center justify-center relative overflow-hidden">
            {dontExample}
          </div>
        </div>
      </div>
      
      <p className="text-sm text-charcoal-700">{explanation}</p>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="flex items-center justify-center gap-3 mb-2">
          <Accessibility className="h-10 w-10 text-sage-600" />
          <h1 className="text-4xl font-bold text-charcoal-900">Accessibility Rules</h1>
        </div>
        <p className="text-lg text-charcoal-600 max-w-3xl mx-auto">
          Comprehensive accessibility guidelines for color contrast, minimum font sizes, alternative text/image use, 
          and disability compliance to ensure all Peak Priorities materials are usable by everyone.
        </p>
      </div>

      {/* System Overview */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <div className="flex items-center gap-3 mb-4">
          <Accessibility className="h-6 w-6 text-sage-600" />
          <h2 className="text-2xl font-bold text-charcoal-900">Accessibility System</h2>
        </div>
        <p className="text-charcoal-700 leading-relaxed mb-6">
          Our accessibility system ensures all Peak Priorities materials meet WCAG 2.1 AA standards and are usable by people with disabilities. 
          Every design decision considers accessibility from the start, creating inclusive experiences for all users.
        </p>
        
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Inclusive</h3>
            <p className="text-sm text-[var(--mustard)]">Design for all users, regardless of ability or disability</p>
          </div>
          <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Compliant</h3>
            <p className="text-sm text-consulting-slate-blue-800">Meet WCAG 2.1 AA standards for accessibility</p>
          </div>
          <div className="bg-sage-50 border-2 border-sage-200 p-4">
            <h3 className="font-bold text-sage-900 mb-2">Testable</h3>
            <p className="text-sm text-sage-900">Clear guidelines that can be verified and tested</p>
          </div>
          <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
            <h3 className="font-bold text-[var(--mustard)] mb-2">Practical</h3>
            <p className="text-sm text-[var(--mustard)]">Actionable guidelines that integrate with design workflows</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex justify-center gap-4 flex-wrap">
        <button
          onClick={() => setActiveTab('contrast')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'contrast'
              ? 'bg-sage-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Eye className="h-5 w-5" />
          Contrast
        </button>
        <button
          onClick={() => setActiveTab('typography')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'typography'
              ? 'bg-sage-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Target className="h-5 w-5" />
          Typography
        </button>
        <button
          onClick={() => setActiveTab('images')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'images'
              ? 'bg-sage-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Star className="h-5 w-5" />
          Images
        </button>
        <button
          onClick={() => setActiveTab('interaction')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'interaction'
              ? 'bg-sage-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <Users className="h-5 w-5" />
          Interaction
        </button>
        <button
          onClick={() => setActiveTab('guidelines')}
          className={`
            flex items-center gap-2 px-6 py-3  font-semibold transition-colors
            ${activeTab === 'guidelines'
              ? 'bg-sage-600 text-white'
              : 'bg-[var(--hover-tint)] text-charcoal-700 hover:bg-[var(--hover-tint)]'
            }
          `}
        >
          <AlertTriangle className="h-5 w-5" />
          Guidelines
        </button>
      </div>

      {/* Color Contrast */}
      {activeTab === 'contrast' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Color Contrast Standards</h2>
            <p className="text-charcoal-700 mb-6">
              WCAG 2.1 AA compliant color contrast ratios ensure text and UI elements are readable by users with visual impairments.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <ContrastExample
                title="Normal Text (AA)"
                foreground="#1F2937"
                background="#FFFFFF"
                ratio="16:1"
                level="AAA"
                example={
                  <div className="bg-[var(--bg-raised)] text-charcoal-900 p-4">
                    <p className="text-base">This text meets AAA contrast standards for normal text</p>
                  </div>
                }
              />

              <ContrastExample
                title="Large Text (AA)"
                foreground="#374151"
                background="#F9FAFB"
                ratio="12:1"
                level="AAA"
                example={
                  <div className="bg-[var(--surface)] text-[var(--text-sec)] p-4">
                    <p className="text-lg font-semibold">This large text meets AAA contrast standards</p>
                  </div>
                }
              />

              <ContrastExample
                title="UI Components (AA)"
                foreground="#FFFFFF"
                background="#3B82F6"
                ratio="4.5:1"
                level="AA"
                example={
                  <div className="bg-consulting-slate-blue-600 text-white p-4">
                    <button className="px-4 py-2 bg-[var(--bg-raised)] text-consulting-slate-blue-600 font-semibold">
                      Accessible Button
                    </button>
                  </div>
                }
              />

              <ContrastExample
                title="Focus Indicators (AA)"
                foreground="#1F2937"
                background="#FEF3C7"
                ratio="4.5:1"
                level="AA"
                example={
                  <div className="bg-[var(--mustard-dim)] text-charcoal-900 p-4 border-2 border-[var(--mustard)]">
                    <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white font-semibold focus:outline-none focus:ring-2 focus:ring-amber-600">
                      Focused Button
                    </button>
                  </div>
                }
              />
            </div>
          </div>

          {/* Contrast Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Contrast Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AccessibilityExample
                title="Minimum Contrast Ratios"
                description="WCAG 2.1 AA compliance requirements"
                compliance="Normal text: 4.5:1, Large text: 3:1, UI components: 3:1"
                rationale="These ratios ensure text is readable by users with visual impairments, including color blindness"
                example={
                  <div className="space-y-2">
                    <div className="bg-[var(--bg-raised)] text-charcoal-900 p-2 rounded text-sm">Normal text (4.5:1)</div>
                    <div className="bg-[var(--hover-tint)] text-[var(--text-sec)] p-2 rounded text-sm">Large text (3:1)</div>
                    <div className="bg-consulting-slate-blue-600 text-white p-2 rounded text-sm">UI component (3:1)</div>
                  </div>
                }
              />

              <AccessibilityExample
                title="Enhanced Contrast (AAA)"
                description="Higher contrast for better accessibility"
                compliance="Normal text: 7:1, Large text: 4.5:1"
                rationale="Enhanced contrast provides better readability for users with more severe visual impairments"
                example={
                  <div className="space-y-2">
                    <div className="bg-[var(--bg-raised)] text-charcoal-900 p-2 rounded text-sm">Enhanced normal text (7:1)</div>
                    <div className="bg-[var(--surface)] text-[var(--text-pri)] p-2 rounded text-sm">Enhanced large text (4.5:1)</div>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Typography */}
      {activeTab === 'typography' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Typography Accessibility</h2>
            <p className="text-charcoal-700 mb-6">
              Typography guidelines that ensure text is readable and accessible to users with visual impairments and reading difficulties.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <TypographyExample
                title="Body Text"
                description="Standard body text for content"
                size="16px"
                weight="400"
                lineHeight="1.5"
                compliance="WCAG 2.1 AA: Minimum 16px font size, 1.5 line height, 4.5:1 contrast ratio"
                example={
                  <div className="text-base leading-relaxed text-charcoal-900">
                    <p>This is accessible body text that meets WCAG 2.1 AA standards for readability and contrast.</p>
                  </div>
                }
              />

              <TypographyExample
                title="Large Text"
                description="Text that can use lower contrast ratios"
                size="18px"
                weight="400"
                lineHeight="1.4"
                compliance="WCAG 2.1 AA: Minimum 18px font size, 3:1 contrast ratio"
                example={
                  <div className="text-lg leading-snug text-charcoal-900">
                    <p>This large text meets accessibility standards with a 3:1 contrast ratio.</p>
                  </div>
                }
              />

              <TypographyExample
                title="Headings"
                description="Hierarchical heading structure"
                size="24px"
                weight="700"
                lineHeight="1.2"
                compliance="WCAG 2.1 AA: Proper heading hierarchy, 4.5:1 contrast ratio"
                example={
                  <div className="space-y-2">
                    <h1 className="text-2xl font-bold text-charcoal-900">Main Heading</h1>
                    <h2 className="text-xl font-semibold text-charcoal-900">Subheading</h2>
                    <h3 className="text-lg font-medium text-charcoal-900">Section Heading</h3>
                  </div>
                }
              />

              <TypographyExample
                title="UI Text"
                description="Interface text and labels"
                size="14px"
                weight="500"
                lineHeight="1.4"
                compliance="WCAG 2.1 AA: Minimum 14px font size, 4.5:1 contrast ratio"
                example={
                  <div className="text-sm font-medium text-charcoal-900">
                    <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white">
                      Accessible Button
                    </button>
                  </div>
                }
              />
            </div>
          </div>

          {/* Typography Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Typography Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AccessibilityExample
                title="Font Size Requirements"
                description="Minimum font sizes for accessibility"
                compliance="Body text: 16px minimum, UI text: 14px minimum, Large text: 18px minimum"
                rationale="Larger font sizes improve readability for users with visual impairments and reading difficulties"
                example={
                  <div className="space-y-2">
                    <p className="text-sm text-charcoal-700">UI text (14px minimum)</p>
                    <p className="text-base text-charcoal-700">Body text (16px minimum)</p>
                    <p className="text-lg text-charcoal-700">Large text (18px minimum)</p>
                  </div>
                }
              />

              <AccessibilityExample
                title="Line Height Standards"
                description="Optimal line spacing for readability"
                compliance="Body text: 1.5 line height, Headings: 1.2 line height, UI text: 1.4 line height"
                rationale="Proper line spacing improves readability and reduces eye strain for all users"
                example={
                  <div className="space-y-2">
                    <p className="text-base leading-relaxed text-charcoal-700">Body text with 1.5 line height for optimal readability</p>
                    <p className="text-lg leading-snug text-charcoal-700">Heading with 1.2 line height for compact display</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Images */}
      {activeTab === 'images' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Image Accessibility</h2>
            <p className="text-charcoal-700 mb-6">
              Guidelines for making images accessible to users with visual impairments through proper alt text and descriptions.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <ImageExample
                title="Decorative Images"
                description="Images that don't convey important information"
                altText=""
                compliance="WCAG 2.1 AA: Empty alt text for decorative images"
                example={
                  <div className="w-16 h-16 bg-gradient-to-br from-consulting-slate-blue-100 to-sage-100 flex items-center justify-center">
                    <span className="text-consulting-slate-blue-600 text-xs">Decorative</span>
                  </div>
                }
              />

              <ImageExample
                title="Informative Images"
                description="Images that convey important information"
                altText="Bar chart showing quarterly revenue growth from Q1 to Q4, with Q4 showing the highest revenue at $2.4M"
                compliance="WCAG 2.1 AA: Descriptive alt text that conveys the same information as the image"
                example={
                  <div className="w-16 h-16 bg-gradient-to-br from-consulting-slate-blue-100 to-sage-100 flex items-center justify-center">
                    <span className="text-consulting-slate-blue-600 text-xs">Chart</span>
                  </div>
                }
              />

              <ImageExample
                title="Complex Images"
                description="Images with multiple data points or complex information"
                altText="Detailed organizational chart showing company hierarchy with CEO at the top, followed by department heads and team members"
                compliance="WCAG 2.1 AA: Detailed description or longdesc attribute for complex images"
                example={
                  <div className="w-16 h-16 bg-gradient-to-br from-consulting-slate-blue-100 to-sage-100 flex items-center justify-center">
                    <span className="text-consulting-slate-blue-600 text-xs">Complex</span>
                  </div>
                }
              />

              <ImageExample
                title="Functional Images"
                description="Images that serve as buttons or links"
                altText="Download report button"
                compliance="WCAG 2.1 AA: Alt text describes the function, not the appearance"
                example={
                  <div className="w-16 h-16 bg-gradient-to-br from-consulting-slate-blue-100 to-sage-100 flex items-center justify-center">
                    <span className="text-consulting-slate-blue-600 text-xs">Button</span>
                  </div>
                }
              />
            </div>
          </div>

          {/* Image Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Image Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AccessibilityExample
                title="Alt Text Best Practices"
                description="Writing effective alternative text"
                compliance="WCAG 2.1 AA: Concise, descriptive alt text that conveys essential information"
                rationale="Alt text provides access to image content for users with visual impairments using screen readers"
                example={
                  <div className="space-y-2">
                    <p className="text-sm text-charcoal-700">✓ "Bar chart showing 25% increase in sales"</p>
                    <p className="text-sm text-charcoal-700">✗ "Chart" or "Image"</p>
                  </div>
                }
              />

              <AccessibilityExample
                title="Image Types"
                description="Different approaches for different image types"
                compliance="WCAG 2.1 AA: Appropriate alt text strategy based on image purpose"
                rationale="Different image types require different accessibility approaches to provide equivalent access"
                example={
                  <div className="space-y-2">
                    <p className="text-sm text-charcoal-700">Decorative: alt=""</p>
                    <p className="text-sm text-charcoal-700">Informative: alt="description"</p>
                    <p className="text-sm text-charcoal-700">Complex: longdesc or detailed description</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Interaction */}
      {activeTab === 'interaction' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Interaction Accessibility</h2>
            <p className="text-charcoal-700 mb-6">
              Guidelines for making interactive elements accessible to users with motor impairments and assistive technologies.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <InteractionExample
                title="Focus Indicators"
                description="Visible focus states for keyboard navigation"
                compliance="WCAG 2.1 AA: Visible focus indicators with 3:1 contrast ratio"
                rationale="Focus indicators help users navigate with keyboard and understand their current position"
                example={
                  <div className="space-y-2">
                    <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2">
                      Focused Button
                    </button>
                    <button className="px-4 py-2 bg-[var(--hover-tint)] text-charcoal-700 focus:outline-none focus:ring-2 focus:ring-consulting-slate-blue-600 focus:ring-offset-2">
                      Secondary Button
                    </button>
                  </div>
                }
              />

              <InteractionExample
                title="Touch Targets"
                description="Adequate size for touch and click targets"
                compliance="WCAG 2.1 AA: Minimum 44x44px touch targets"
                rationale="Larger touch targets are easier to use for users with motor impairments and on mobile devices"
                example={
                  <div className="space-y-2">
                    <button className="w-12 h-12 bg-consulting-slate-blue-600 text-white flex items-center justify-center">
                      <span className="text-sm">✓</span>
                    </button>
                    <button className="w-8 h-8 bg-[var(--hover-tint)] text-charcoal-700 flex items-center justify-center">
                      <span className="text-xs">✗</span>
                    </button>
                  </div>
                }
              />

              <InteractionExample
                title="Keyboard Navigation"
                description="All functionality accessible via keyboard"
                compliance="WCAG 2.1 AA: All interactive elements accessible via keyboard"
                rationale="Keyboard navigation is essential for users who cannot use a mouse or touch screen"
                example={
                  <div className="space-y-2">
                    <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      Tab to focus
                    </button>
                    <button className="px-4 py-2 bg-sage-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      Enter to activate
                    </button>
                  </div>
                }
              />

              <InteractionExample
                title="Error Handling"
                description="Clear error messages and validation"
                compliance="WCAG 2.1 AA: Clear error identification and instructions"
                rationale="Clear error messages help all users understand and correct mistakes"
                example={
                  <div className="space-y-2">
                    <div className="border-2 border-[var(--coral)] p-2">
                      <input type="email" placeholder="Enter email" className="w-full p-2 border-0 focus:outline-none" />
                    </div>
                    <p className="text-sm text-[var(--coral)]">Please enter a valid email address</p>
                  </div>
                }
              />
            </div>
          </div>

          {/* Interaction Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Interaction Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <AccessibilityExample
                title="Touch Target Sizes"
                description="Minimum sizes for interactive elements"
                compliance="WCAG 2.1 AA: Minimum 44x44px touch targets with adequate spacing"
                rationale="Larger touch targets reduce errors and improve usability for users with motor impairments"
                example={
                  <div className="space-y-2">
                    <button className="w-12 h-12 bg-consulting-slate-blue-600 text-white flex items-center justify-center">
                      <span className="text-sm">44px</span>
                    </button>
                    <p className="text-xs text-charcoal-600">Minimum recommended size</p>
                  </div>
                }
              />

              <AccessibilityExample
                title="Focus Management"
                description="Proper focus handling and order"
                compliance="WCAG 2.1 AA: Logical focus order and visible focus indicators"
                rationale="Proper focus management helps users navigate efficiently and understand the interface structure"
                example={
                  <div className="space-y-2">
                    <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      First
                    </button>
                    <button className="px-4 py-2 bg-sage-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      Second
                    </button>
                    <button className="px-4 py-2 bg-[var(--mustard)] text-white focus:outline-none focus:ring-2 focus:ring-amber-600">
                      Third
                    </button>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Guidelines */}
      {activeTab === 'guidelines' && (
        <div className="space-y-8">
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Accessibility Guidelines</h2>
            <p className="text-charcoal-700 mb-6">
              Essential guidelines for creating accessible Peak Priorities materials that comply with WCAG 2.1 AA standards.
            </p>
            
            <div className="grid md:grid-cols-2 gap-6">
              <DoDontExample
                title="Color Usage"
                doExample={
                  <div className="space-y-2">
                    <div className="bg-consulting-slate-blue-600 text-white p-2 rounded text-sm">Primary button</div>
                    <div className="bg-sage-600 text-white p-2 rounded text-sm">Success message</div>
                  </div>
                }
                dontExample={
                  <div className="space-y-2">
                    <div className="bg-[var(--coral)] text-white p-2 rounded text-sm">Error message</div>
                    <div className="bg-[var(--coral)] text-white p-2 rounded text-sm">Required field</div>
                  </div>
                }
                explanation="Use color as a secondary indicator, not the only way to convey information. Always provide text or icons alongside color."
              />

              <DoDontExample
                title="Text Contrast"
                doExample={
                  <div className="bg-[var(--bg-raised)] text-charcoal-900 p-2 rounded text-sm">
                    High contrast text that meets WCAG AA standards
                  </div>
                }
                dontExample={
                  <div className="bg-[var(--hover-tint)] text-[var(--text-ter)] p-2 rounded text-sm">
                    Low contrast text that fails accessibility standards
                  </div>
                }
                explanation="Ensure sufficient contrast between text and background colors. Use tools to verify contrast ratios meet WCAG standards."
              />

              <DoDontExample
                title="Interactive Elements"
                doExample={
                  <button className="w-12 h-12 bg-consulting-slate-blue-600 text-white flex items-center justify-center">
                    <span className="text-sm">✓</span>
                  </button>
                }
                dontExample={
                  <button className="w-6 h-6 bg-consulting-slate-blue-600 text-white flex items-center justify-center">
                    <span className="text-xs">✗</span>
                  </button>
                }
                explanation="Make interactive elements large enough to be easily tapped or clicked. Minimum 44x44px for touch targets."
              />

              <DoDontExample
                title="Focus Indicators"
                doExample={
                  <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white focus:outline-none focus:ring-2 focus:ring-amber-600 focus:ring-offset-2">
                    Visible Focus
                  </button>
                }
                dontExample={
                  <button className="px-4 py-2 bg-consulting-slate-blue-600 text-white focus:outline-none">
                    No Focus
                  </button>
                }
                explanation="Provide clear visual focus indicators for keyboard navigation. Use high contrast colors and sufficient thickness."
              />
            </div>
          </div>

          {/* Testing Guidelines */}
          <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
            <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Testing Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Automated Testing</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Color Contrast</h4>
                    <p className="text-sm text-[var(--mustard)]">Use tools like WebAIM Contrast Checker to verify contrast ratios</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Alt Text</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Check that all images have appropriate alt text</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">Keyboard Navigation</h4>
                    <p className="text-sm text-sage-800">Test all functionality using only keyboard navigation</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-charcoal-900 mb-4">Manual Testing</h3>
                <div className="space-y-4">
                  <div className="bg-[var(--mustard-dim)] border-2 border-[var(--mustard)] p-4">
                    <h4 className="font-medium text-[var(--mustard)] mb-2">Screen Reader</h4>
                    <p className="text-sm text-[var(--mustard)]">Test with screen readers like NVDA or VoiceOver</p>
                  </div>
                  
                  <div className="bg-consulting-slate-blue-50 border-2 border-consulting-slate-blue-200 p-4">
                    <h4 className="font-medium text-consulting-slate-blue-900 mb-2">Zoom Testing</h4>
                    <p className="text-sm text-consulting-slate-blue-800">Test at 200% zoom to ensure content remains usable</p>
                  </div>
                  
                  <div className="bg-sage-50 border-2 border-sage-200 p-4">
                    <h4 className="font-medium text-sage-900 mb-2">User Testing</h4>
                    <p className="text-sm text-sage-800">Include users with disabilities in testing processes</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Implementation Guidelines */}
      <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 border-2 border-sage-200 p-8 text-center">
        <h3 className="text-2xl font-bold text-charcoal-900 mb-3">
          Implementation Guidelines
        </h3>
        <p className="text-charcoal-700 max-w-3xl mx-auto mb-6">
          Use these accessibility guidelines consistently across all Peak Priorities materials. 
          Accessibility should be considered from the start of every design process, not added as an afterthought.
        </p>
        <div className="flex gap-4 justify-center flex-wrap">
          <button className="flex items-center gap-2 px-6 py-3 bg-sage-600 text-white font-semibold hover:bg-sage-700 transition-colors">
            <Download className="h-5 w-5" />
            Download Guidelines
          </button>
          <button className="flex items-center gap-2 px-6 py-3 bg-[var(--bg-raised)] text-charcoal-900 border-2 border-sage-600 font-semibold hover:bg-sage-50 transition-colors">
            <Copy className="h-5 w-5" />
            Request Audit
          </button>
        </div>
      </div>
    </div>
  );
};




